import { PREFISSI_OPTIONS } from '@/src/lib/constants/options'
import type { FlowConfig } from '@/src/lib/flow-engine/types'

export const bookingStudentFlow: FlowConfig = {
  id: 'booking-student',
  path: '/booking-student',
  title: 'flows.booking-student.title',
  steps: [
    // ── Step 1.1 — Entry / Registration ─────────────────────
    {
      id: 1,
      slug: 'registration',
      group: 1,
      title: 'steps.registration.title',
      shortTitle: 'steps.registration.shortTitle',
      summaryTitle: 'summary.studentGroups.1',
      layout: 'centered',
      fields: [
        {
          key: 'nome',
          label: 'fields.nome_student.label',
          type: 'text',
          required: true,
          placeholder: 'fields.nome_student.placeholder',
          validation: { minLength: 2 }
        },
        {
          key: 'cognome',
          label: 'fields.cognome_student.label',
          type: 'text',
          required: true,
          placeholder: 'fields.cognome_student.placeholder',
          validation: { minLength: 2 }
        },
        {
          key: 'privacy',
          label: 'fields.privacy.label',
          checkboxText: 'fields.privacy.checkboxText',
          type: 'checkbox',
          required: true,
          validation: { mustBeTrue: true }
        },
        {
          key: 'autorizzazione_servizi',
          label: 'fields.autorizzazione_servizi.label',
          checkboxText: 'fields.autorizzazione_servizi.checkboxText',
          type: 'checkbox',
          required: false,
          validation: { mustBeTrue: false }
        },
        {
          key: 'occupazione',
          label: 'fields.occupazione.label',
          type: 'hidden',
          defaultValue: 0
        }
      ]
    },

    // ── Step 1.2 — Contact info ──────────────────────
    {
      id: 2,
      slug: 'contacts',
      group: 1,
      title: 'steps.contacts.title',
      titleTemplate: 'steps.contacts.titleTemplate',
      shortTitle: 'steps.contacts.shortTitle',
      description: 'steps.contacts.description',
      layout: 'centered',
      fields: [
        {
          key: 'email',
          label: 'fields.email_student.label',
          type: 'email',
          required: true,
          placeholder: 'fields.email_student.placeholder'
        },
        {
          key: 'prefisso',
          label: 'fields.prefisso.label',
          type: 'select',
          required: true,
          sortOptions: false,
          options: PREFISSI_OPTIONS
        },
        {
          key: 'cellulare',
          label: 'fields.cellulare_student.label',
          type: 'tel',
          required: true,
          validation: { minLength: 6, maxLength: 15 }
        }
      ]
    },

    // ── Step 1.3 — City selection ────────────────────────────
    {
      id: 3,
      saveStep: 1,
      saveOnNext: true,
      slug: 'city',
      group: 1,
      title: 'steps.city.title',
      shortTitle: 'steps.city.shortTitle',
      description: 'steps.city.description',
      layout: 'form',
      fullWidth: true,
      fields: [
        {
          key: 'citta_1',
          label: 'fields.citta_1.label',
          type: 'city-grid',
          required: true,
          remoteOptions: {
            endpoint: '/api/booking-student/lookups/cities/',
            valueKey: 'slug',
            labelKey: 'name',
            imageBasePath: '/images'
          }
        }
      ]
    },

    // ── Step 2 — Dati personali ──────────────────────────────
    {
      id: 4,
      saveStep: 2,
      saveOnNext: true,
      slug: 'personal-data',
      group: 2,
      title: 'steps.personal-data.title',
      shortTitle: 'steps.personal-data.shortTitle',
      summaryTitle: 'summary.studentGroups.2',
      description: 'steps.personal-data.description',
      layout: 'form',
      showSideSummary: true,
      fields: [
        {
          key: 'nazione_residenza',
          label: 'fields.nazione_residenza.label',
          type: 'select',
          required: true,
          colSpan: 6,
          remoteOptions: {
            endpoint: '/api/lookups/countries/',
            valueKey: 'value',
            labelKey: 'label'
          }
        },
        {
          key: 'sesso',
          label: 'fields.sesso.label',
          type: 'select',
          required: true,
          colSpan: 6,
          sortOptions: false,
          options: [
            { value: 'M', label: 'options.sesso.M' },
            { value: 'F', label: 'options.sesso.F' }
          ]
        },
        {
          key: 'data_nascita',
          label: 'fields.data_nascita.label',
          type: 'date-picker-airbnb',
          required: true,
          colSpan: 6,
          validation: { minAge: 16 }
        }
      ]
    },

    // ── Step 3 — Alloggio ──────────────────────────────────
    {
      id: 5,
      saveStep: 3,
      saveOnNext: true,
      slug: 'accommodation',
      group: 3,
      title: 'steps.accommodation.title',
      shortTitle: 'steps.accommodation.shortTitle',
      summaryTitle: 'summary.studentGroups.3',
      description: 'steps.accommodation.description',
      layout: 'form',
      showSideSummary: true,
      fields: [
        {
          key: 'sede_camplus_1',
          label: 'fields.sede_camplus_1.label',
          type: 'select',
          required: true,
          colSpan: 6,
          remoteOptions: {
            endpoint: '/api/booking-student/lookups/structures/',
            valueKey: 'slug',
            labelKey: 'name',
            extraFields: {
              bando_1: 'bando',
              tipologia_1: 'tipologia'
            },
            dependsOn: 'citta_1'
          }
        },
        {
          key: 'bando_1',
          label: 'fields.bando_1.label',
          type: 'hidden',
          defaultValue: false
        },
        {
          key: 'tipologia_1',
          label: 'fields.tipologia_1.label',
          type: 'hidden',
          defaultValue: false
        },
        {
          key: 'sistemazione_1',
          label: 'fields.sistemazione_1.label',
          type: 'select',
          required: true,
          colSpan: 6,
          sortOptions: false,
          remoteOptions: {
            endpoint: '/api/booking-student/lookups/rooms/',
            valueKey: 'slug',
            labelKey: 'name',
            dependsOn: 'sede_camplus_1'
          }
        },
        {
          key: 'checkin',
          label: 'fields.checkin.label',
          type: 'date-picker-airbnb',
          required: true,
          colSpan: 6,
          validation: { disabledMonths: [6, 7] }
        },
        {
          key: 'durata',
          label: 'fields.durata.label',
          type: 'select',
          required: true,
          colSpan: 6,
          sortOptions: false,
          options: [
            { value: '6', label: 'options.durata.6' },
            { value: '12', label: 'options.durata.12' }
          ]
        },
        {
          key: 'codice_convenzione',
          label: 'fields.codice_convenzione.label',
          type: 'text',
          required: false,
          colSpan: 12,
          placeholder: 'fields.codice_convenzione.placeholder',
          validation: { maxLength: 500 }
        }
      ]
    },

    // ── Step 4 — University & Academic info ─────────────────
    {
      id: 6,
      saveStep: 4,
      saveOnNext: true,
      slug: 'university',
      group: 4,
      title: 'steps.university.title',
      shortTitle: 'steps.university.shortTitle',
      summaryTitle: 'summary.studentGroups.4',
      description: 'steps.university.description',
      layout: 'form',
      showSideSummary: true,
      fields: [
        {
          key: 'nazionalita_diploma',
          label: 'fields.nazionalita_diploma.label',
          type: 'select',
          required: true,
          colSpan: 6,
          sortOptions: false,
          options: [
            { value: '1', label: 'options.nazionalita_diploma.1' },
            { value: '0', label: 'options.nazionalita_diploma.0' }
          ],
          visibleWhenAll: [
            {
              field: 'tipologia_1',
              operator: 'eq',
              value: 'Collegio'
            }
          ]
        },
        {
          key: 'universita',
          label: 'fields.universita.label',
          type: 'select',
          required: true,
          colSpan: 6,
          remoteOptions: {
            endpoint: '/api/lookups/universities/',
            valueKey: 'slug',
            labelKey: 'name'
          }
        },
        {
          key: 'area_disciplinare',
          label: 'fields.area_disciplinare.label',
          type: 'select',
          required: true,
          colSpan: 6,
          remoteOptions: {
            endpoint: '/api/lookups/faculties/',
            valueKey: 'slug',
            labelKey: 'name'
          }
        },
        // anno_corso slug mapping (remote endpoint /codes/annocorso):
        //   1  → 1° Laurea Triennale
        //   2  → 2° Laurea Triennale
        //   3  → 3° Laurea Triennale
        //   4  → 1° Laurea Magistrale
        //   5  → 2° Laurea Magistrale
        //   6  → 1° Laurea Ciclo Unico
        //   7  → 2° Laurea Ciclo Unico
        //   8  → 3° Laurea Ciclo Unico
        //   9  → 4° Laurea Ciclo Unico
        //   10 → 5° Laurea Ciclo Unico
        //   11 → 6° Laurea Ciclo Unico
        //   12 → Erasmus
        //   13 → Altro (ex Master/Dottorato)
        {
          key: 'anno_corso',
          label: 'fields.anno_corso.label',
          type: 'select',
          required: true,
          sortOptions: false,
          colSpan: 6,
          helperText: 'fields.anno_corso.helperText',
          remoteOptions: {
            endpoint: '/api/lookups/annocorso/',
            valueKey: 'slug',
            labelKey: 'name'
          }
        },
        {
          key: 'voto_diploma',
          label: 'fields.voto_diploma.label',
          type: 'select',
          required: true,
          sortOptions: false,
          colSpan: 6,
          options: [
            { value: 'Esame non ancora sostenuto', label: 'options.voto_diploma.non-sostenuto' },
            ...Array.from({ length: 41 }, (_, i) => ({
              value: String(60 + i),
              label: String(60 + i)
            })),
            { value: '100 e lode', label: 'options.voto_diploma.100L' }
          ],
          visibleWhenAny: [
            [
              {
                field: 'tipologia_1',
                operator: 'eq',
                value: 'Collegio'
              },
              {
                field: 'nazionalita_diploma',
                operator: 'eq',
                value: '1'
              },
              {
                field: 'anno_corso',
                operator: 'in',
                value: ['1', '6']
              }
            ],
            [
              {
                field: 'bando_1',
                operator: 'eq',
                value: true
              },
              {
                field: 'nazione_residenza',
                operator: 'eq',
                value: 'ITA'
              },
              {
                field: 'anno_corso',
                operator: 'in',
                value: ['1', '6']
              }
            ]
          ]
        },
        {
          key: 'punteggio_ammissione',
          label: 'fields.punteggio_ammissione.label',
          type: 'select',
          required: true,
          sortOptions: false,
          colSpan: 6,
          options: [
            {
              value: 'Non lo ho ancora sostenuto',
              label: 'options.punteggio_ammissione.non-sostenuto'
            },
            ...Array.from({ length: 100 }, (_, i) => ({
              value: String(100 - i),
              label: String(100 - i)
            })),
            { value: 'Altro', label: 'options.punteggio_ammissione.altro' }
          ],
          visibleWhenAll: [
            {
              field: 'tipologia_1',
              operator: 'eq',
              value: 'Collegio'
            },
            {
              field: 'nazionalita_diploma',
              operator: 'eq',
              value: '1'
            },
            {
              field: 'anno_corso',
              operator: 'in',
              value: ['1', '6']
            }
          ]
        },
        {
          key: 'punteggio_ammissione_altro',
          label: 'fields.punteggio_ammissione_altro.label',
          type: 'text',
          required: true,
          colSpan: 6,
          visibleWhen: {
            field: 'punteggio_ammissione',
            operator: 'eq',
            value: 'Altro'
          }
        },
        {
          key: 'numero_cfu',
          label: 'fields.numero_cfu.label',
          type: 'number',
          required: true,
          colSpan: 6,
          validation: { min: 0, max: 40 },
          visibleWhenAny: [
            [
              {
                field: 'tipologia_1',
                operator: 'eq',
                value: 'Collegio'
              },
              {
                field: 'nazionalita_diploma',
                operator: 'eq',
                value: '1'
              },
              {
                field: 'anno_corso',
                operator: 'in',
                value: ['1', '6']
              },
              {
                field: 'voto_diploma',
                operator: 'eq',
                value: 'Esame non ancora sostenuto'
              }
            ],
            [
              {
                field: 'bando_1',
                operator: 'eq',
                value: true
              },
              {
                field: 'nazione_residenza',
                operator: 'eq',
                value: 'ITA'
              },
              {
                field: 'anno_corso',
                operator: 'in',
                value: ['1', '6']
              },
              {
                field: 'voto_diploma',
                operator: 'eq',
                value: 'Esame non ancora sostenuto'
              }
            ]
          ]
        },
        {
          key: 'media_universita',
          label: 'fields.media_universita.label',
          type: 'number',
          required: true,
          colSpan: 6,
          validation: { min: 18, max: 30 },
          helperText: 'fields.media_universita.helperText',
          visibleWhenAny: [
            [
              {
                field: 'tipologia_1',
                operator: 'eq',
                value: 'Collegio'
              },
              {
                field: 'anno_corso',
                operator: 'in',
                value: ['2', '3', '7', '8', '9', '10', '11', '5']
              }
            ],
            [
              {
                field: 'bando_1',
                operator: 'eq',
                value: true
              },
              {
                field: 'nazione_residenza',
                operator: 'eq',
                value: 'ITA'
              },
              {
                field: 'anno_corso',
                operator: 'in',
                value: ['2', '3', '7', '8', '9', '10', '11', '4', '5']
              }
            ]
          ]
        },
        {
          key: 'voto_laurea',
          label: 'fields.voto_laurea.label',
          type: 'select',
          required: true,
          colSpan: 6,
          sortOptions: false,
          options: [
            ...Array.from({ length: 45 }, (_, i) => ({
              value: String(66 + i),
              label: String(66 + i)
            })),
            { value: '110L', label: 'options.voto_laurea.110L' }
          ],
          visibleWhenAny: [
            [
              { field: 'tipologia_1', operator: 'eq', value: 'Collegio' },
              { field: 'anno_corso', operator: 'eq', value: '4' }
            ],
            [
              { field: 'tipologia_1', operator: 'eq', value: 'Collegio' },
              { field: 'anno_corso', operator: 'eq', value: '13' },
              { field: 'nazionalita_diploma', operator: 'eq', value: '1' }
            ],
            [
              { field: 'bando_1', operator: 'eq', value: true },
              { field: 'nazione_residenza', operator: 'eq', value: 'ITA' },
              { field: 'anno_corso', operator: 'eq', value: '13' }
            ]
          ]
        },
        {
          key: 'certificazione_italiano',
          label: 'fields.certificazione_italiano.label',
          type: 'select',
          required: true,
          colSpan: 6,
          sortOptions: false,
          options: [
            { value: '0', label: 'options.certificazione_italiano.non-certificazione' },
            { value: 'C2', label: 'C2' },
            { value: 'C1', label: 'C1' },
            { value: 'B2', label: 'B2' },
            { value: 'B1', label: 'B1' },
            { value: 'A2', label: 'A2' },
            { value: 'A1', label: 'A1' }
          ],
          visibleWhenAll: [
            {
              field: 'nazione_residenza',
              operator: 'neq',
              value: 'ITA'
            },
            {
              field: 'bando_1',
              operator: 'eq',
              value: true
            }
          ]
        },
        {
          key: 'certificazione_altra_lingua',
          label: 'fields.certificazione_altra_lingua.label',
          type: 'select',
          required: true,
          colSpan: 6,
          sortOptions: false,
          options: [
            { value: 'Inglese', label: 'options.certificazione_altra_lingua.inglese' },
            { value: 'Spagnolo', label: 'options.certificazione_altra_lingua.spagnolo' },
            { value: 'Francese', label: 'options.certificazione_altra_lingua.francese' },
            { value: 'Tedesco', label: 'options.certificazione_altra_lingua.tedesco' },
            { value: 'Portoghese', label: 'options.certificazione_altra_lingua.portoghese' },
            { value: 'Cinese', label: 'options.certificazione_altra_lingua.cinese' },
            { value: 'Giapponese', label: 'options.certificazione_altra_lingua.giapponese' },
            { value: 'Coreano', label: 'options.certificazione_altra_lingua.coreano' },
            { value: 'Russo', label: 'options.certificazione_altra_lingua.russo' },
            { value: 'Arabo', label: 'options.certificazione_altra_lingua.arabo' },
            { value: 'Olandese', label: 'options.certificazione_altra_lingua.olandese' },
            { value: 'Turco', label: 'options.certificazione_altra_lingua.turco' },
            { value: 'Polacco', label: 'options.certificazione_altra_lingua.polacco' },
            { value: 'Ucraino', label: 'options.certificazione_altra_lingua.ucraino' },
            { value: 'Altro', label: 'options.certificazione_altra_lingua.altro' },
            { value: '0', label: 'options.certificazione_altra_lingua.non-certificazione' }
          ],
          visibleWhenAll: [
            {
              field: 'nazione_residenza',
              operator: 'neq',
              value: 'ITA'
            },
            {
              field: 'bando_1',
              operator: 'eq',
              value: true
            }
          ]
        },
        {
          key: 'certificazione_altra_lingua_altro',
          label: 'fields.certificazione_altra_lingua_altro.label',
          type: 'text',
          required: true,
          colSpan: 6,
          visibleWhen: {
            field: 'certificazione_altra_lingua',
            operator: 'eq',
            value: 'Altro'
          }
        },
        {
          key: 'livello_certificato_altra_lingua',
          label: 'fields.livello_certificato_altra_lingua.label',
          type: 'select',
          required: true,
          colSpan: 6,
          sortOptions: false,
          options: [
            { value: 'C2', label: 'C2' },
            { value: 'C1', label: 'C1' },
            { value: 'B2', label: 'B2' },
            { value: 'B1', label: 'B1' },
            { value: 'A2', label: 'A2' },
            { value: 'A1', label: 'A1' }
          ],
          visibleWhen: {
            field: 'certificazione_altra_lingua',
            operator: 'notIn',
            value: ['', '0']
          }
        },
        {
          key: 'bando_inps',
          label: 'fields.bando_inps.label',
          type: 'select',
          required: true,
          colSpan: 6,
          options: [
            { value: '1', label: 'options.bando_inps.1' },
            { value: '0', label: 'options.bando_inps.0' }
          ],
          helperText: 'fields.bando_inps.helperText',
          sortOptions: false,
          visibleWhenAll: [
            {
              field: 'tipologia_1',
              operator: 'eq',
              value: 'Collegio'
            },
            {
              field: 'nazionalita_diploma',
              operator: 'eq',
              value: '1'
            }
          ]
        },
        {
          key: 'fascia_isee',
          label: 'fields.fascia_isee.label',
          type: 'select',
          required: true,
          colSpan: 6,
          placeholder: 'fields.fascia_isee.placeholder',
          remoteOptions: {
            endpoint: '/api/lookups/isee-ranges/',
            valueKey: 'slug',
            labelKey: 'name'
          },
          visibleWhen: {
            field: 'bando_inps',
            operator: 'eq',
            value: '1'
          }
        },
        {
          key: 'numero_pratica_inps',
          label: 'fields.numero_pratica_inps.label',
          type: 'text',
          required: false,
          colSpan: 6,
          visibleWhen: {
            field: 'bando_inps',
            operator: 'eq',
            value: '1'
          }
        },
        {
          key: 'concorrere_reddito',
          label: 'fields.concorrere_reddito.label',
          type: 'select',
          required: true,
          colSpan: 6,
          options: [
            { value: '1', label: 'options.concorrere_reddito.1' },
            { value: '0', label: 'options.concorrere_reddito.0' }
          ],
          sortOptions: false,
          helperText: 'fields.concorrere_reddito.helperText',
          visibleWhen: {
            field: 'bando_inps',
            operator: 'eq',
            value: '0'
          }
        },
        {
          key: 'valore_isee',
          label: 'fields.valore_isee.label',
          type: 'number',
          required: true,
          colSpan: 6,
          placeholder: 'fields.valore_isee.placeholder',
          visibleWhenAll: [
            {
              field: 'bando_inps',
              operator: 'eq',
              value: '0'
            },
            {
              field: 'concorrere_reddito',
              operator: 'eq',
              value: '1'
            }
          ]
        },
        {
          key: 'riduzione_merito',
          label: 'fields.riduzione_merito.label',
          type: 'select',
          required: true,
          colSpan: 6,
          options: [
            { value: '1', label: 'options.riduzione_merito.1' },
            { value: '0', label: 'options.riduzione_merito.0' }
          ],
          visibleWhen: {
            field: 'bando_inps',
            operator: 'eq',
            value: '0'
          }
        }
      ]
    },

    // ── Step 5 — Additional info + Declarations ────────────
    {
      id: 7,
      slug: 'completion',
      group: 5,
      title: 'steps.completion.title',
      shortTitle: 'steps.completion.shortTitle',
      summaryTitle: 'summary.studentGroups.5',
      description: 'steps.completion.description',
      layout: 'form',
      showSideSummary: true,
      fields: [
        {
          key: 'conoscenza_camplus',
          label: 'fields.conoscenza_camplus.label',
          type: 'select',
          required: true,
          options: [
            { value: 'eventi', label: 'options.conoscenza_camplus.eventi' },
            { value: 'parenti-amici', label: 'options.conoscenza_camplus.parenti-amici' },
            { value: 'social-media', label: 'options.conoscenza_camplus.social-media' },
            {
              value: 'sito-universita',
              label: 'options.conoscenza_camplus.sito-universita'
            },
            {
              value: 'ricerca-online',
              label: 'options.conoscenza_camplus.ricerca-online'
            },
            {
              value: 'campagna-pubblicitaria',
              label: 'options.conoscenza_camplus.campagna-pubblicitaria'
            },
            { value: 'altro', label: 'options.conoscenza_camplus.altro' }
          ]
        },
        {
          key: 'specifica_conoscenza',
          label: 'fields.specifica_conoscenza.label',
          type: 'text',
          required: true,
          placeholder: 'fields.specifica_conoscenza.placeholder',
          visibleWhen: {
            field: 'conoscenza_camplus',
            operator: 'eq',
            value: 'altro'
          }
        },
        {
          key: 'testo_accoglienza',
          label: 'fields.testo_accoglienza.label',
          type: 'textarea',
          required: false,
          placeholder: 'fields.testo_accoglienza.placeholder',
          validation: { maxLength: 500 },
          visibleWhenAll: [
            {
              field: 'tipologia_1',
              operator: 'neq',
              value: 'Collegio'
            }
          ]
        },
        {
          key: 'autopresentazione',
          label: 'fields.autopresentazione.label',
          type: 'textarea',
          required: true,
          colSpan: 12,
          placeholder: 'fields.autopresentazione.placeholder',
          validation: { maxLength: 500 },
          visibleWhenAll: [
            {
              field: 'tipologia_1',
              operator: 'eq',
              value: 'Collegio'
            }
          ]
        },
        {
          key: 'numero_matricola',
          label: 'fields.numero_matricola.label',
          type: 'checkbox',
          required: true,
          validation: { mustBeTrue: true }
        },
        {
          key: 'condanne_penali',
          label: 'fields.condanne_penali.label',
          type: 'checkbox',
          required: true,
          validation: { mustBeTrue: true }
        },
        {
          key: 'patologie',
          label: 'fields.patologie.label',
          type: 'checkbox',
          required: true,
          validation: { mustBeTrue: true }
        }
      ]
    },

    // ── Step 6 — Summary ────────────────────────────────────
    {
      id: 8,
      saveStep: 5,
      saveOnNext: true,
      slug: 'summary',
      group: 6,
      title: 'steps.summary.title',
      shortTitle: 'steps.summary.shortTitle',
      description: 'steps.summary.description',
      layout: 'summary',
      fields: [],
      isFinal: true
    }
  ],
  successRedirectRules: [
    {
      name: 'Waitlist Students',
      redirectUrl: {
        it: 'https://www.camplus.it/feedback-domanda-di-ammissione-studenti-lista-attesa/',
        en: 'https://www.camplus.it/en/waiting-list-feedback-student-admission-application/',
        fr: 'https://www.camplus.it/fr/commentaires-sur-la-liste-dattente-demande-dadmission-des-etudiants/',
        es: 'https://www.camplus.it/es/comentarios-sobre-la-lista-de-espera-solicitud-de-admision-de-estudiantes/'
      },
      conditions: [{ field: 'lista_attesa', operator: 'eq', value: true }]
    },
    {
      name: 'PNRR Students',
      redirectUrl: {
        it: 'https://www.camplus.it/feedback-domanda-di-ammissione-studenti-pnrr/',
        en: 'https://www.camplus.it/en/call-for-application-feedback-students-pnrr/',
        fr: 'https://www.camplus.it/fr/feedback-demande-dadmission-etudiants-pnrr/',
        es: 'https://www.camplus.it/es/feedback-solicitud-de-admision-estudiantes-pnrr/'
      },
      conditions: [{ field: 'bando_1', operator: 'eq', value: true }]
    }
  ],
  fallbackUrl: {
    it: 'https://www.camplus.it/feedback-domanda-di-ammissione-studenti/',
    en: 'https://www.camplus.it/en/call-for-application-feedback-students/',
    fr: 'https://www.camplus.it/fr/feedback-demande-dadmission-etudiants/',
    es: 'https://www.camplus.it/es/feedback-solicitud-de-admision-estudiantes/'
  }
}

/** Helpers */
export function getStudentStepBySlug(slug: string) {
  return bookingStudentFlow.steps.find((s) => s.slug === slug)
}

export function getStudentStepById(id: number) {
  return bookingStudentFlow.steps.find((s) => s.id === id)
}

export function getStudentStepBySaveStep(saveStep: number) {
  return [...bookingStudentFlow.steps].reverse().find((s) => (s.saveStep ?? s.id) === saveStep)
}
